import Swal, { SweetAlertOptions, SweetAlertResult } from "sweetalert2";

/**
 * Base SweetAlert2 configuration for consistent styling across the app
 * These settings are optimized for mobile isolation and z-index management
 */
export const swalBaseConfig: SweetAlertOptions = {
  customClass: {
    container: "swal2-z-top",
    popup: "z-[100000]",
  },
  allowOutsideClick: false,
  allowEscapeKey: false,
  backdrop: "rgba(0,0,0,0.6)",
  heightAuto: false,
  scrollbarPadding: false,
  stopKeydownPropagation: true,
  focusConfirm: true,
  reverseButtons: false,
  buttonsStyling: true,
};

interface ConfirmDialogOptions {
  title: string;
  text?: string;
  html?: string;
  icon?: "warning" | "question" | "error" | "info" | "success";
  confirmButtonText: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
}

/**
 * Generic confirmation dialog with consistent styling
 * Returns true if confirmed, false otherwise
 */
export async function showConfirmDialog(
  options: ConfirmDialogOptions
): Promise<boolean> {
  const result = await Swal.fire({
    ...swalBaseConfig,
    title: options.title,
    text: options.text,
    html: options.html,
    icon: options.icon || "question",
    showCancelButton: true,
    confirmButtonText: options.confirmButtonText,
    cancelButtonText: options.cancelButtonText || "Cancelar",
    confirmButtonColor: options.confirmButtonColor || "#3b82f6",
    cancelButtonColor: options.cancelButtonColor || "#6b7280",
  });

  return result.isConfirmed;
}

/**
 * Show error dialog with consistent styling
 */
export async function showErrorDialog(
  title: string,
  message: string
): Promise<SweetAlertResult> {
  return Swal.fire({
    ...swalBaseConfig,
    title,
    html: `<div class="text-left">
      <p>Detalles del error:</p>
      <pre class="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">${message}</pre>
    </div>`,
    icon: "error",
  });
}

// ===== Pre-configured confirmation dialogs =====

/**
 * Confirm marking an order as test
 */
export async function confirmMarkAsTest(): Promise<boolean> {
  return showConfirmDialog({
    title: "¿Marcar como Test?",
    text: "Esta nota de venta será marcada como test y no se contará en estadísticas.",
    icon: "question",
    confirmButtonText: "Sí, marcar como test",
    confirmButtonColor: "#6b7280",
    cancelButtonColor: "#3085d6",
  });
}

/**
 * Confirm marking an order as paid
 */
export async function confirmMarkAsPaid(): Promise<boolean> {
  return showConfirmDialog({
    title: "¿Marcar como Pagado?",
    text: "Esta nota de venta será marcada como pagada.",
    icon: "question",
    confirmButtonText: "Sí, marcar como pagado",
    confirmButtonColor: "#16a34a",
    cancelButtonColor: "#3085d6",
  });
}

/**
 * Confirm marking an order as unpaid
 */
export async function confirmMarkAsUnpaid(): Promise<boolean> {
  return showConfirmDialog({
    title: "¿Marcar como NO Pagado?",
    text: "Esta nota de venta será marcada como NO pagada. Esta acción se puede revertir.",
    icon: "warning",
    confirmButtonText: "Sí, marcar como NO pagado",
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#3085d6",
  });
}

/**
 * Confirm approving a document for invoicing
 */
export async function confirmApproveDocument(): Promise<boolean> {
  return showConfirmDialog({
    title: "¿Aprobar Documento?",
    text: "Esta nota de venta será enviada a Por Facturar.",
    icon: "question",
    confirmButtonText: "Sí, aprobar documento",
    confirmButtonColor: "#3b82f6",
    cancelButtonColor: "#6b7280",
  });
}

/**
 * Confirm force approving an order without payment validation (superadmin only)
 */
export async function confirmForceApproveWithoutPayment(): Promise<boolean> {
  return showConfirmDialog({
    title: "Aprobar Sin Validar Pagos",
    html: `
      <div class="text-left text-sm">
        <p class="text-amber-600 font-medium mb-2">Solo para superadmin</p>
        <p>Esta acción aprobará la nota de venta <strong>SIN validar los pagos</strong>.</p>
        <p class="mt-2 text-gray-500">Los pagos permanecerán sin verificar.</p>
      </div>
    `,
    icon: "warning",
    confirmButtonText: "Sí, aprobar sin validar",
    confirmButtonColor: "#d97706",
  });
}

/**
 * Confirm deleting an item
 */
export async function confirmDelete(itemName?: string): Promise<boolean> {
  return showConfirmDialog({
    title: "¿Eliminar?",
    text: itemName
      ? `¿Está seguro de eliminar "${itemName}"?`
      : "¿Está seguro de eliminar este elemento?",
    icon: "warning",
    confirmButtonText: "Sí, eliminar",
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
  });
}

/**
 * Confirm recovering a rejected order
 */
export async function confirmRecoverOrder(
  previousStatus: string
): Promise<boolean> {
  return showConfirmDialog({
    title: "¿Recuperar Nota de Venta?",
    html: `
      <div class="text-left text-sm">
        <p>Esta nota de venta rechazada será recuperada y volverá al estado:</p>
        <p class="mt-2 font-medium text-blue-600">${previousStatus}</p>
      </div>
    `,
    icon: "question",
    confirmButtonText: "Sí, recuperar",
    confirmButtonColor: "#3b82f6",
  });
}
